// cohorts/manage services
// Pure async functions only: no React, no hooks, no state. Parse at this boundary.
import { privateApi } from "@core/libs/axios";
import { parseWith } from "@core/parse";
import { CohortSchema, type Cohort } from "../../_shared/types";
import type { CohortChanges } from "../types";

/** Every call here answers with the cohort as it now is. */
async function send(
  method: "PATCH" | "PUT" | "POST",
  path: string,
  body: object,
  idempotencyKey: string
): Promise<Cohort> {
  const dto = await privateApi
    .request<unknown>({
      method,
      url: path,
      data: body,
      headers: { "Idempotency-Key": idempotencyKey },
    })
    .then((r) => r.data);
  return parseWith(CohortSchema, dto, `${method} ${path}`);
}

/**
 * `PATCH /cohorts/{id}/`: change the fields that differ. Needs the cohort's current `revision` (a
 * stale form is refused with `409 cohort_revision_conflict`) and an `Idempotency-Key`.
 */
export function updateCohort(
  id: string,
  changes: CohortChanges,
  expectedRevision: number,
  idempotencyKey: string
): Promise<Cohort> {
  return send(
    "PATCH",
    `/cohorts/${id}/`,
    { ...changes, expected_revision: expectedRevision },
    idempotencyKey
  );
}

/**
 * `PUT /cohorts/{id}/responsible-administrator/`: name another responsible Administrator (President
 * only). The server emails the new one and tells the previous one the cohort is no longer theirs.
 */
export function appointAdministrator(
  id: string,
  administratorId: string,
  expectedRevision: number,
  idempotencyKey: string
): Promise<Cohort> {
  return send(
    "PUT",
    `/cohorts/${id}/responsible-administrator/`,
    {
      user_id: administratorId,
      expected_revision: expectedRevision,
    },
    idempotencyKey
  );
}

/** `POST /cohorts/{id}/archive/` (President only): the cohort becomes read-only. */
export function archiveCohort(
  id: string,
  expectedRevision: number,
  idempotencyKey: string
): Promise<Cohort> {
  return send(
    "POST",
    `/cohorts/${id}/archive/`,
    { expected_revision: expectedRevision },
    idempotencyKey
  );
}

/** `POST /cohorts/{id}/restore/` (President only): an archived cohort is editable again. */
export function restoreCohort(
  id: string,
  expectedRevision: number,
  idempotencyKey: string
): Promise<Cohort> {
  return send(
    "POST",
    `/cohorts/${id}/restore/`,
    { expected_revision: expectedRevision },
    idempotencyKey
  );
}
